// Small "Reorder on Amazon" link rendered next to OTC + supplement
// medications. Disabled (renders null) when:
//   - VITE_AMAZON_AFFILIATE_TAG is empty (feature off), or
//   - the medication is a prescription product (classify -> 'prescription').
//
// Privacy: we send only the medication name and the public affiliate
// tag in the URL. No user id, no medication id, no metadata. Amazon's
// own search page handles the rest.
//
// FTC: visible disclosure required when affiliate revenue is involved.
// The link has a short "may earn a commission" tag underneath.
//
// When OTCHealthMart launches its own catalog, the URL host can be
// swapped to otchealthmart.com for products we sell; everything else
// (placement, copy, disclosure) stays put.

import { env } from '../env';

export type ProductKind = 'prescription' | 'otc' | 'supplement' | 'unknown';

interface Props {
  name: string;
  /**
   * If known from the OCR pipeline (Gemini dosing hint), pass it
   * directly. Otherwise pass undefined and we infer from the name.
   */
  kind?: ProductKind;
  /** "compact" = inline link, "card" = boxed CTA. Default compact. */
  variant?: 'compact' | 'card';
}

// Common OTC ingredients + brand names. If a medication's name contains
// any of these tokens (word-boundary, case-insensitive) we treat it as
// OTC. Conservative: when unsure, classify as 'prescription' so we
// don't surface affiliate links on Rx products.
const OTC_TOKENS = [
  'acetaminophen',
  'tylenol',
  'ibuprofen',
  'advil',
  'motrin',
  'aspirin',
  'bayer',
  'excedrin',
  'aleve',
  'naproxen',
  'benadryl',
  'diphenhydramine',
  'claritin',
  'loratadine',
  'zyrtec',
  'cetirizine',
  'allegra',
  'fexofenadine',
  'sudafed',
  'pseudoephedrine',
  'mucinex',
  'guaifenesin',
  'dayquil',
  'nyquil',
  'robitussin',
  'dextromethorphan',
  'omeprazole',
  'prilosec',
  'pepcid',
  'famotidine',
  'tums',
  'rolaids',
  'maalox',
  'mylanta',
  'pepto',
  'bismuth',
  'imodium',
  'loperamide',
  'miralax',
  'polyethylene glycol',
  'dulcolax',
  'bisacodyl',
  'senna',
  'metamucil',
  'simethicone',
  'gas-x',
  'milk of magnesia',
  'visine',
  'systane',
  'preparation h',
  'hydrocortisone',
  'cortizone',
  'neosporin',
  'bacitracin',
  'lotrimin',
  'monistat',
  'clotrimazole',
  'flonase',
  'fluticasone',
  'nasacort',
  'triamcinolone',
];

const SUPPLEMENT_TOKENS = [
  'vitamin',
  'multivitamin',
  'supplement',
  'cholecalciferol',
  'ergocalciferol',
  'cyanocobalamin',
  'methylcobalamin',
  'biotin',
  'folate',
  'folic acid',
  'iron',
  'ferrous sulfate',
  'calcium',
  'magnesium',
  'zinc',
  'selenium',
  'potassium',
  'fish oil',
  'omega',
  'flaxseed',
  'turmeric',
  'curcumin',
  'glucosamine',
  'chondroitin',
  'probiotic',
  'melatonin',
  'coq10',
  'coenzyme q10',
  'echinacea',
  'elderberry',
  'ginseng',
  'ginkgo',
  'garlic',
  'milk thistle',
  'st johns wort',
  'valerian',
  'ashwagandha',
  'collagen',
  'fiber',
  'psyllium',
];

export function classifyProduct(name: string): ProductKind {
  if (!name) return 'unknown';
  const haystack = name.toLowerCase();
  if (SUPPLEMENT_TOKENS.some((t) => haystack.includes(t))) return 'supplement';
  if (OTC_TOKENS.some((t) => haystack.includes(t))) return 'otc';
  return 'prescription';
}

export function AmazonReorderLink({ name, kind, variant = 'compact' }: Props): JSX.Element | null {
  const tag = env.amazonAffiliateTag;
  if (!tag) return null;
  const resolved = kind ?? classifyProduct(name);
  // Don't surface affiliate links on prescriptions (most Rx items on
  // Amazon are not legitimate and we don't want to suggest the user
  // bypass their pharmacy). Also skip unknown to be conservative.
  if (resolved !== 'otc' && resolved !== 'supplement') return null;
  const query = encodeURIComponent(name);
  const href = `https://www.amazon.com/s?k=${query}&tag=${encodeURIComponent(tag)}`;
  const label = resolved === 'supplement' ? 'Reorder on Amazon' : 'Reorder on Amazon';

  if (variant === 'card') {
    return (
      <div className="amazon-link amazon-link--card">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer nofollow sponsored"
          className="amazon-link__cta"
        >
          <AmazonIcon /> {label}
        </a>
        <p className="amazon-link__disclosure">As an Amazon Associate, we may earn a commission.</p>
      </div>
    );
  }
  return (
    <span className="amazon-link amazon-link--compact">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow sponsored"
        className="amazon-link__compact"
      >
        <AmazonIcon /> {label}
      </a>
      <span className="amazon-link__disclosure-inline">paid link</span>
    </span>
  );
}

function AmazonIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <title>Amazon</title>
      <path
        d="M3 17c4 3 11 4 16 0M5 19c1 1 3 1.5 5 1"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="9" cy="11" r="2.2" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="15" cy="11" r="2.2" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}
